import axios from "axios";
import { useCallback, useState } from "react";
import { getBackendUrl } from "../../../../../../util/envFn";
import { TodoList } from "../../../../types/types";
import { API_PREFIX_TODO_LISTS } from "../../constants";

const useTodoList_Server = (currentWorkspaceId: string | null, refreshWorkspace: () => void) => {
    const createTodoList = async (title?: string): Promise<TodoList> => {
        if (!currentWorkspaceId) throw new Error("No workspace selected");

        const response = await axios.post(getBackendUrl(`${API_PREFIX_TODO_LISTS}/${currentWorkspaceId}`), title ?? 'New List');
        refresh();
        return response.data;
    };

    const getTodoLists = useCallback(async (): Promise<TodoList[]> => {
        if (!currentWorkspaceId) return [];

        const response = await axios.get(getBackendUrl(`${API_PREFIX_TODO_LISTS}/${currentWorkspaceId}`));
        return response.data;
    }, [currentWorkspaceId]);

    const updateListTitle = async (todoListId: string, title: string): Promise<boolean> => {
        const response = await axios.put(
            getBackendUrl(`${API_PREFIX_TODO_LISTS}/${todoListId}`),
            { title: title }
        );
        refresh();
        return !!response.data;
    };

    const deleteTodoList = async (todoListId: string): Promise<boolean> => {
        const response = await axios.delete(getBackendUrl(`${API_PREFIX_TODO_LISTS}/${todoListId}`));
        refresh();
        return !!response.data;
    };

    const reorderList = async (sourceIndex: number, destinationIndex: number): Promise<boolean> => {
        if (!currentWorkspaceId) return false;

        const response = await axios.put(
            getBackendUrl(`${API_PREFIX_TODO_LISTS}/${currentWorkspaceId}/reorder`),
            { sourceIndex, destinationIndex }
        );
        refreshWorkspace();
        return !!response.data;
    };

    const [todoLists, setTodoLists] = useState<TodoList[]>([]);
    const [todoListsMap, setTodoListsMap] = useState<Map<string, TodoList>>(new Map());

    const refresh = useCallback(async () => {
        try {
            const todoLists = await getTodoLists();
            setTodoLists(todoLists);
            setTodoListsMap(new Map(todoLists.map(todoList => [todoList.id, todoList])));
        } catch (error) {
            // console.error("Error refreshing todo lists:");
        }
    }, [getTodoLists]);

    const clear = useCallback(async () => {
        setTodoLists([]);
        setTodoListsMap(new Map());
    }, []);

    return {
        refresh,
        clear,
        todoLists,
        todoListsMap,
        createTodoList,
        updateListTitle,
        reorderList,
        deleteTodoList,
    }
}

export default useTodoList_Server;